import cx from 'classnames';
import * as _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import type { FC, MouseEvent, VFC } from 'react';

import { RevisionLicenseConclusionScope } from '../../../../shared/model-typings/revisionlicenseconclusion';
import useGetGlobalConclusions from '../../../hooks/api/correctionsV2/useGetGlobalConclusions';
import {
  lookupProjectAndLicenses,
  removeLicense,
  removeMatch,
  revertOverride,
} from '../../../redux/globalLicenseCorrections/actions';
import { selectors } from '../../../redux/globalLicenseCorrections/slice';
import { Badge, Icon, IconButton, icons } from '../../generic';
import Accordion from '../../generic/Accordion';
import Flex from '../../generic/Flex';
import Loader from '../../generic/Loader';
import { LoadingBoundary } from '../../generic/LoadingBoundary';
import useConcludeLicense from '../../projects/dependenciesV2/DependencyDrawer/content/DependencyLicenses/hooks/useConcludeLicense';
import useUnconcludeLicense from '../../projects/dependenciesV2/DependencyDrawer/content/DependencyLicenses/hooks/useUnconcludeLicense';
import type { GlobalConclusionResult } from '../../../../shared/types/licenses';
import type { MatchAndOverrides } from '../../../redux/globalLicenseCorrections/types';
import type {
  Match,
  MatchOverride,
  MatchOverrideWithHashFilter,
  MatchOverrideWithLicenseFilter,
  MatchOverrideWithNewLicense,
  RevisionLicense,
} from '../../../types';

import css from './LicenseEditor.module.less';

type NewLicenseOverrideProps = {
  override: MatchOverrideWithNewLicense;
  isLoading: boolean;
  onRevert: (override: MatchOverride) => (e: MouseEvent) => void;
};

const NewLicenseOverride: VFC<NewLicenseOverrideProps> = ({ override, isLoading, onRevert }) =>
  <div className={cx(css.override, css.override__created)}>
    <span>
      <span className={css.icon}>
        <Icon icon={icons.warning} />
      </span>
      This license will always be present due to an override.
    </span>
    <IconButton
      id="revert-license"
      icon={icons.undo}
      tooltip="Revert this override"
      onClick={onRevert(override)}
      isDisabled={isLoading}
    />
  </div>;

type LicenseChangeOverrideProps = {
  override: MatchOverrideWithLicenseFilter;
  isLoading: boolean;
  onRevert: (override: MatchOverride) => (e: MouseEvent) => void;
};

const LicenseChangeOverride: VFC<LicenseChangeOverrideProps> = ({ override, isLoading, onRevert }) =>
  <div className={cx(css.override, {
    [css.override__reclassified]: override.newLicenseIdOrRemove,
    [css.override__removed]: !override.newLicenseIdOrRemove,
  })}>
    {
      override.newLicenseIdOrRemove
        ?
          <span>
            <span className={css.icon}>
              <Icon icon={icons.warning} />
            </span>
            All matches for this license will be reclassified as {override.newLicenseIdOrRemove}
          </span>
        :
          <span>
            <span className={css.icon}>
              <Icon icon={icons.warning} />
            </span>
            All matches for this license will be removed
          </span>
    }
    <IconButton
      id="revert-license"
      icon={icons.undo}
      tooltip="Revert this override"
      onClick={onRevert(override)}
      isDisabled={isLoading}
      />
  </div>;

/** A comparison function to sort by path. */
function compareMatchAndOverrideEntryByPath(a: MatchAndOverrides, b: MatchAndOverrides) {
  const aPath = a.match?.path || a.overrides[0]?.matchFilter.path || a.overrides[0]?.matchFilter.matchIdentityHash || '';
  const bPath = b.match?.path || b.overrides[0]?.matchFilter.path || b.overrides[0]?.matchFilter.matchIdentityHash || '';

  if (aPath < bPath) {
    return -1;
  }
  if (aPath > bPath) {
    return 1;
  }
  return 0;
}

type Props = {
  revisionLocator: string;
};

/**
 * Lists licenses for the currently selected project/revision and allows the
 * user to configure overrides.
 */
export const LicenseEditor: VFC<Props> = ({ revisionLocator }) => {
  const licensesAndOverridesValue = useSelector(selectors.licensesAndOverridesSelector);
  const { data: conclusions, isLoading: isLoadingConclusions } = useGetGlobalConclusions(revisionLocator);

  if (isLoadingConclusions) {
    return <Loader />;
  }

  return (
    <LoadingBoundary
      asyncValue={licensesAndOverridesValue}
      renderValue={({ value: licensesAndOverrides }) => {
        return (
          <div>
            {
              licensesAndOverrides.map(licenseAndOverrides =>
                <LicenseMatches
                  key={licenseAndOverrides.licenseId}
                  licenseId={licenseAndOverrides.licenseId}
                  licenses={licenseAndOverrides.licenses}
                  newLicenseOverrides={licenseAndOverrides.newOverrides}
                  matches={licenseAndOverrides.matches}
                  licenseOverrides={licenseAndOverrides.licenseOverrides}
                  conclusions={conclusions}
                  dependencyRevisionLocator={revisionLocator}
                />,
              )
            }
          </div>);
      }}
      renderEmpty={<div>empty</div>}
      renderError={({ errors }) => <div>error: ${errors.join('. ')}</div>}
      renderLoading={<Loader />}
      />);
};

type LicenseMatchesProps = {
  licenseId: string;
  licenses: RevisionLicense[];
  newLicenseOverrides: MatchOverrideWithNewLicense[];
  matches: MatchAndOverrides[];
  licenseOverrides: MatchOverrideWithLicenseFilter[];
  conclusions?: GlobalConclusionResult;
  dependencyRevisionLocator: string;
};

/**
 * Lists a license as an accordion that contains its matched paths.
 */
const LicenseMatches: FC<LicenseMatchesProps> = (
  { licenseId, licenses, newLicenseOverrides, matches, licenseOverrides, conclusions, dependencyRevisionLocator },
) => {
  const dispatch = useDispatch();

  const project = useSelector(selectors.projectSelector);

  const { mutateAsync: concludeLicense } = useConcludeLicense({
    dependencyRevisionLocator,
    scope: {
      scope: RevisionLicenseConclusionScope.GLOBAL,
    },
  });
  const { mutateAsync: unconcludeLicense } = useUnconcludeLicense({
    dependencyRevisionLocator,
    scope: {
      scope: RevisionLicenseConclusionScope.GLOBAL,
    },
  });

  const onConcludeLicense = async (e: MouseEvent, chosenLicense: string, action: 'conclude' | 'unconclude') => {
    if (action === 'conclude') {
      concludeLicense({
        chosenLicense,
        chooseParams: {
          dependencyRevisionLocator,
          scope: {
            scope: RevisionLicenseConclusionScope.GLOBAL,
          },
        },
      });
    } else {
      unconcludeLicense({
        chosenLicense,
        chooseParams: {
          dependencyRevisionLocator,
          scope: {
            scope: RevisionLicenseConclusionScope.GLOBAL,
          },
        },
      });
    }

    // Prevent the accordion from toggling.
    e.stopPropagation();

    // Refresh the licenses
    await dispatch(lookupProjectAndLicenses(dependencyRevisionLocator));
  };

  const onRemove = (e: MouseEvent) => {
    if (!project.value) {
      return;
    }
    dispatch(removeLicense(project.value, licenseId));
    e.stopPropagation();
  };

  const onRevert = (override: MatchOverride) => (e: MouseEvent) => {
    dispatch(revertOverride(override));
    e.stopPropagation();
  };

  const isGlobalConclusion = conclusions?.globalConcludedLicenses.includes(licenseId);
  const isBaseConclusion = conclusions?.baseConcludedLicenses.includes(licenseId);

  return (
    <Accordion key={licenseId} id={`licenses-${licenseId}`} isInitiallyOpen>
      <Accordion.Target>
        <div className={css.licenseHeader}>
          <Flex gap="8px">
            <span className={cx({
              [css.licenseOverride]: !_.isEmpty(licenseOverrides),
            })}>
              {licenseId ?? licenseOverrides.find(override => override.matchFilter.licenseId)?.matchFilter.licenseId ?? '(no license)'}
            </span>
            {isGlobalConclusion && (
              <Badge icon={icons.warning} color="green">Concluded by override</Badge>
            )}
            {isBaseConclusion && <Badge icon={icons.check} color="gray">Base conclusion</Badge>}
          </Flex>
          <Flex gap="8px">
            {!isGlobalConclusion && (
              <span className={css.concludeButton}>
                <IconButton
                  id="conclude-license"
                  icon={icons.checkCircleO}
                  onClick={(e) => onConcludeLicense(e, licenseId, 'conclude')}
                  tooltip={`Conclude ${licenseId}`}
                />
              </span>
            )}
            {isGlobalConclusion && (
              <span className={css.unconcludeButton}>
                <IconButton
                  id="unconclude-license"
                  icon={icons.exclamationCircleRegular}
                  onClick={(e) => onConcludeLicense(e, licenseId, 'unconclude')}
                  tooltip={`Unconclude ${licenseId}`}
                />
              </span>
            )}
            {
            _.isEmpty(licenseOverrides) && _.isEmpty(newLicenseOverrides) && !_.isEmpty(licenses) &&
              <span className={css.removeButton}>
                <IconButton
                  id="remove-license"
                  icon={icons.remove}
                  tooltip="Remove this license"
                  onClick={onRemove}
                  isDisabled={project.isLoading}
                  />
              </span>
          }
          </Flex>
        </div>
      </Accordion.Target>
      <Accordion.Body>
        {
          newLicenseOverrides.map(newLicenseOverride =>
            <NewLicenseOverride
              key={newLicenseOverride.id}
              override={newLicenseOverride}
              isLoading={project.isLoading}
              onRevert={onRevert} />)
        }
        {
          licenseOverrides.map(override =>
            <LicenseChangeOverride
              key={override.id}
              override={override}
              isLoading={project.isLoading}
              onRevert={onRevert} />)
        }
        {
          _.isEmpty(matches)
            ?
              <div className={cx(css.matches, css.noMatches)}>
                {
                  newLicenseOverrides
                    ? `No matches found.  The license should show up as declared during the next analysis.`
                    : `No matches found.  Empty licenses are generally removed during analysis.`
                }
              </div>
            :
              <ul className={css.matches}>
                { matches
                  .sort(compareMatchAndOverrideEntryByPath)
                  .map(({ match, overrides: matchOverrides }: MatchAndOverrides) => {
                    return (<MatchItem
                    // One of these two should always contain a hash, so the fall-through is just for typing.
                      key={match?.matchIdentityHash || matchOverrides[0]?.matchFilter.matchIdentityHash || 'no-hash'}
                      match={match}
                      overrides={matchOverrides}
                      isOverridden={!_.isEmpty(matchOverrides) || !_.isEmpty(licenseOverrides)} />);
                  })}
              </ul>
        }
      </Accordion.Body>
    </Accordion>
  );
};

type MatchItemProps = {
  match?: Match,
  overrides: MatchOverrideWithHashFilter[],
  isOverridden: boolean,
};

/** An item in the list of matches */
const MatchItem: FC<MatchItemProps> = ({ match, overrides, isOverridden }) => {
  const dispatch = useDispatch();

  const project = useSelector(selectors.projectSelector);

  const onRemove = () => {
    if (!project.value || !match) {
      return;
    }
    dispatch(removeMatch(project.value, match));
  };

  const onRevert = async () => {
    await Promise.all(overrides.map(matchOverride =>
      dispatch(revertOverride(matchOverride)),
    ));
  };

  return (
    <li
      // One of these two should always contain a hash, so the fall-through is just for typing.
      key={match?.matchIdentityHash || overrides[0]?.matchFilter.matchIdentityHash || 'no-hash'}
      className={cx(css.match, {
        // This handles the case that the override is there, but nothing matches it anymore.
        [css.itemNotPresent]: match === null,
      })}>
      <span>
        <span className={cx({
          [css.matchOverride]: isOverridden,
          [css.itemNotPresent]: !match,
        })}>
          {match?.path || overrides[0]?.matchFilter.path || overrides[0]?.matchFilter.matchIdentityHash }
        </span>
        { isOverridden && match && <span> (pending reanalysis)</span>}
      </span>
      {
        !_.isEmpty(overrides) &&
          <IconButton
            id="revert-match"
            icon={icons.undo}
            tooltip="Revert this override (remove this license)"
            onClick={onRevert}
            isDisabled={project.isLoading}
            />
      }
      {
        !isOverridden &&
        <span className={css.removeButton}>
          <IconButton
            id="remove-match"
            icon={icons.remove}
            tooltip="Remove this match"
            onClick={onRemove}
            isDisabled={project.isLoading}
            />
        </span>
      }
    </li>
  );
};
